import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import { listAgents } from './api';
import { AgentCard } from './AgentCard';
import { AgentsMap } from './AgentsMap';

type View = 'board' | 'map';

const VIEW_STORAGE_KEY = 'sigil:agents:view';

export function AgentsListPage() {
  const { org } = useCurrentOrg();
  const orgId = org?.id ?? '';
  const nav = useNavigate();

  const [view, setView] = useState<View>(() => {
    if (typeof window === 'undefined') return 'board';
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
    return stored === 'map' ? 'map' : 'board';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(VIEW_STORAGE_KEY, view);
    }
  }, [view]);

  const agents = useQuery({
    queryKey: ['agents', orgId],
    queryFn: () => listAgents(orgId),
    enabled: !!orgId,
  });

  const visible = (agents.data ?? []).filter((a) => a.status !== 'archived');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-semibold tracking-tightish text-ink-primary">Agents</h1>
          <p className="mt-1 text-sm text-ink-secondary">
            Typed autonomous agents bound to Sigil wallets. Switch between a status board for
            day-to-day ops and a map view to see how agents share wallets.
          </p>
        </div>
        <Button onClick={() => nav('/agents/new')}>+ New agent</Button>
      </div>

      <div className="flex items-center justify-end">
        <ViewSwitcher view={view} onChange={setView} />
      </div>

      {agents.isLoading && (
        <div className="rounded-xl border border-line-soft bg-surface-white p-12 text-center text-sm text-ink-tertiary">
          Loading…
        </div>
      )}

      {agents.data && visible.length === 0 && (
        <div className="rounded-xl border border-dashed border-line-soft bg-surface-soft/30 p-12 text-center">
          <p className="text-sm text-ink-tertiary">
            No agents yet. Click <strong className="text-ink-primary">New agent</strong> to create your
            first one.
          </p>
        </div>
      )}

      {visible.length > 0 && view === 'board' && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {visible.map((a) => (
            <AgentCard key={a.id} orgId={orgId} agent={a} />
          ))}
        </div>
      )}

      {visible.length > 0 && view === 'map' && (
        <AgentsMap orgId={orgId} agents={visible} />
      )}
    </div>
  );
}

function ViewSwitcher({ view, onChange }: { view: View; onChange: (v: View) => void }) {
  return (
    <div className="inline-flex rounded-lg border border-line-soft bg-surface-white p-0.5">
      <ViewButton active={view === 'board'} onClick={() => onChange('board')}>
        Board
      </ViewButton>
      <ViewButton active={view === 'map'} onClick={() => onChange('map')}>
        Map
      </ViewButton>
    </div>
  );
}

function ViewButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
        active
          ? 'bg-bit2me text-white'
          : 'text-ink-secondary hover:bg-surface-soft hover:text-ink-primary'
      }`}
    >
      {children}
    </button>
  );
}
